import type {
  CurriculumResponse,
  GroupResponse,
} from '@lms/api-client/backend';
import { Button } from '@lms/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@lms/ui/dialog';
import { Input } from '@lms/ui/input';
import { Label } from '@lms/ui/label';
import { PlusIcon } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { useTranslations } from 'use-intl';
import { SimpleSelect } from '@/components/simple-select';
import { SubmitButton } from '@/components/submit-button';
import type { Site } from '@/features/branding/brand-provider';
import { addCode } from '@/features/codes/codes-api';
import { createCodeSchema } from '../actions.schema';

export function AddCodeForm({
  site,
  curriculums,
  groups,
  onSuccess,
}: {
  site: Site;
  groups: GroupResponse[];
  curriculums: CurriculumResponse[];
  onSuccess: () => void;
}) {
  const t = useTranslations('admin');
  const t_code = useTranslations('code');
  const [group, setGroup] = useState(site.hasGroups ? '-1' : `${groups[0].id}`);
  const [curriculum, setCurriculum] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);
  const [pending, setPending] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(undefined);

    const submission = createCodeSchema.safeParse({
      curriculum,
      group,
      location: `${site.id}`,
      name,
    });
    if (!submission.success) {
      setError(t('failed_to_create_code'));
      return;
    }

    setPending(true);
    try {
      const result = await addCode(submission.data);
      if (result.ok) {
        onSuccess();
      } else {
        setError(
          'messageKey' in result ? t(result.messageKey) : result.message,
        );
      }
    } finally {
      setPending(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      {site.hasGroups ? (
        <div className="flex flex-col gap-2">
          <Label>{t(`${site.groupAlias}_singular`)}</Label>
          <SimpleSelect
            name="group"
            value={group}
            onValueChange={setGroup}
            placeholder={t(`${site.groupAlias}_singular`)}
            options={[
              {
                value: '-1',
                label: t_code('tenant', {
                  groupAlias: t(`${site.groupAlias}_singular`).toLowerCase(),
                }),
              },
              { value: '-3', label: t_code('supplier') },
              { value: '-2', label: t_code('supplier_freetext') },
              ...groups.map((s) => ({ value: `${s.id}`, label: s.name })),
            ]}
          />
        </div>
      ) : null}
      <div className="flex flex-col gap-2">
        <Label>{t('curriculum')}</Label>
        <SimpleSelect
          name="curriculum"
          value={curriculum}
          onValueChange={setCurriculum}
          placeholder={t('curriculum')}
          options={curriculums.map((c) => ({
            value: `${c.id}`,
            label: c.name,
          }))}
        />
      </div>
      <div className="flex flex-col gap-2">
        <Label htmlFor="code-name">{t('code_name_optional')}</Label>
        <Input
          id="code-name"
          name="name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t('code_name_placeholder')}
        />
      </div>
      {error && <div className="text-destructive text-sm">{error}</div>}
      <SubmitButton
        label={t('create_code')}
        pending={pending}
        disabled={!curriculum}
      />
    </form>
  );
}

export default function AddCodeDialog({
  site,
  curriculums,
  groups,
  onCreated,
}: {
  site: Site;
  curriculums: CurriculumResponse[];
  groups: GroupResponse[];
  onCreated: () => void;
}) {
  const t = useTranslations('admin');
  const [open, setOpen] = useState(false);
  const title = t('add', { what: t('code').toLowerCase() });

  const onSuccess = () => {
    setOpen(false);
    onCreated();
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <PlusIcon className="h-5 w-5" />
          {title}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <AddCodeForm
          site={site}
          curriculums={curriculums}
          groups={groups}
          onSuccess={onSuccess}
        />
      </DialogContent>
    </Dialog>
  );
}
